import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios"
import {
  getCookie,
  setSessionCookie,
  AUTH_ACCESS_TOKEN_COOKIE,
  AUTH_REFRESH_TOKEN_COOKIE,
} from "@/lib/cookies"
import {
  formatRateLimitMessage,
  getRetryAfterSeconds,
  sleep,
} from "@/lib/rateLimit"

const dbBaseURL = import.meta.env.VITE_API_URL ?? ""
const ragBaseURL = import.meta.env.VITE_RAG_API_URL ?? ""

const defaultHeaders = {
  "Content-Type": "application/json",
}

/** Axios instance for auth API (login, register, refresh). No 401 retry on this instance. */
export const dbApi = axios.create({
  baseURL: dbBaseURL,
  headers: defaultHeaders,
})

/** Axios instance for LLM/chat API. */
export const ragApi = axios.create({
  baseURL: ragBaseURL,
  headers: defaultHeaders,
})

// ----- Auth refresh (used by interceptors and silent refresh) -----

interface RefreshResponse {
  tokens: { accessToken: string; refreshToken: string }
}

let refreshPromise: Promise<string | null> | null = null
let refreshJustFailed = false

/** Call after successful login or when we have a valid session so a future 401 can trigger refresh again. */
export function clearRefreshFailure(): void {
  refreshJustFailed = false
}

/** Perform token refresh. Uses dbApi (no 401 interceptor). Returns new access token or null. */
export async function doRefresh(): Promise<string | null> {
  const refreshToken = getCookie(AUTH_REFRESH_TOKEN_COOKIE)
  if (!refreshToken) return null
  try {
    const { data } = await dbApi.post<RefreshResponse>("/api/auth/refresh", {
      refreshToken,
    })
    setSessionCookie(AUTH_ACCESS_TOKEN_COOKIE, data.tokens.accessToken)
    setSessionCookie(AUTH_REFRESH_TOKEN_COOKIE, data.tokens.refreshToken)
    refreshJustFailed = false
    return data.tokens.accessToken
  } catch {
    refreshJustFailed = true
    return null
  }
}

/** Single refresh in flight; waiters get the same promise. Returns new access token or null. */
export function getRefreshPromise(): Promise<string | null> {
  if (refreshJustFailed) return Promise.resolve(null)
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// ----- Auth refs (set by AuthProvider so interceptors can get token and logout) -----

let authRef: {
  getAccessToken: () => string | null
  logout: () => void
} = {
  getAccessToken: () => null,
  logout: () => {},
}

export function setAuthRef(
  getAccessToken: () => string | null,
  logout: () => void
): void {
  authRef = { getAccessToken, logout }
}

export function getAccessToken(): string | null {
  return authRef.getAccessToken()
}

// ----- dbApi: attach Bearer token to every request (e.g. GET /chats, GET /chat/:id) -----

dbApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = authRef.getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

dbApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }
    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }
    originalRequest._retry = true
    const newToken = await getRefreshPromise()
    if (!newToken) {
      authRef.logout()
      return Promise.reject(error)
    }
    return dbApi.request(originalRequest)
  }
)

// ----- ragApi: attach Bearer token to every request -----

ragApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = authRef.getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ----- ragApi: on 401, refresh once and retry (or logout) -----

ragApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }
    originalRequest._retry = true
    const newToken = await getRefreshPromise()
    if (!newToken) {
      authRef.logout()
      return Promise.reject(error)
    }
    return ragApi.request(originalRequest)
  }
)

function attachRateLimitRetry(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _rateLimitRetry?: boolean
      }
      if (
        !originalRequest ||
        error.response?.status !== 429 ||
        originalRequest._rateLimitRetry ||
        (originalRequest.method ?? "get").toUpperCase() !== "GET"
      ) {
        return Promise.reject(error)
      }

      const retryAfter = getRetryAfterSeconds(error.response.headers)
      const waitMs = Math.min((retryAfter ?? 1) * 1000, 60_000)
      originalRequest._rateLimitRetry = true
      await sleep(waitMs)
      return instance.request(originalRequest)
    }
  )
}

attachRateLimitRetry(dbApi)
attachRateLimitRetry(ragApi)

/** Get user-facing error message from an unknown caught value (axios or generic). */
export function getApiErrorMessage(e: unknown): string {
  if (axios.isAxiosError(e)) {
    if (e.response?.status === 429) {
      const body = e.response.data as { error?: string; message?: string } | undefined
      if (body?.error) return body.error
      if (body?.message) return body.message
      return formatRateLimitMessage(getRetryAfterSeconds(e.response.headers))
    }
    const err = e.response?.data as { error?: string } | undefined
    return err?.error ?? e.message ?? "Something went wrong"
  }
  return e instanceof Error ? e.message : "Something went wrong"
}
